import React from "react";
import { Link } from "react-router-dom";
import { CalendarDays, IndianRupee, MapPin, Ticket } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { resolveImage } from "@/lib/media";
import { FavoriteButton } from "@/components/FavoriteButton";
import { longDate, rupees } from "@/lib/format";

export function whenText(event, lang) {
  // `starts_on` / `ends_on`, not `_at`. The API sends an event's dates as DATEs under `_on`
  // (`EventOut`); `_at` is reserved for timestamps like `placed_at`. Reading `_at` here left
  // both blank, so every event card and the events list showed no date at all.
  const start = longDate(event.starts_on, lang);
  const end = longDate(event.ends_on, lang);
  return end && end !== start ? `${start} – ${end}` : start;
}

export function EventCard({ event }) {
  const { lang, t } = useApp();
  const title = localized(lang, event, "title");
  return (
    <Link
      to={`/events/${event.id}`}
      data-testid={`event-card-${event.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="relative h-40 overflow-hidden bg-muted">
        {event.cover_image_url ? (
          <img
            src={resolveImage(event.cover_image_url)}
            alt={title}
            width={400}
            height={160}
            loading="lazy" decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-4xl font-semibold text-muted-foreground/40">
            {title.slice(0, 2).toUpperCase()}
          </div>
        )}
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold">
          <CalendarDays size={12} className="text-[hsl(var(--primary))]" /> {whenText(event, lang)}
        </span>
        <FavoriteButton targetType="event" targetId={event.id} className="absolute right-3 top-3" />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <h3 className="font-heading text-lg font-semibold leading-snug">{title}</h3>
        {event.venue && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin size={12} /> {localized(lang, event, "venue")}
          </span>
        )}
        <div className="mt-auto pt-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs">
            {event.entry_type === "free" ? <Ticket size={12} /> : <IndianRupee size={12} />}
            {event.entry_type === "free" ? t("events.free") : rupees({ minor_units: event.entry_fee?.minor_units })}
          </span>
        </div>
      </div>
    </Link>
  );
}
